<template>
  <Motion
    :initial="{ opacity: 0, x: -20 }"
    :animate="{ opacity: 1, x: 0 }"
    :transition="{ duration: 0.5, delay: 0.3, type: 'spring' }"
  >
    <div class="bg-white/80  backdrop-blur-sm rounded-xl p-4 sm:p-6 md:p-8 shadow-lg">
      <div class="max-w-2xl mx-auto relative z-20 px-2 sm:px-4">
        <div class="max-w-xl mx-auto text-center mb-8">
          <div class="inline-flex items-center justify-center p-2 bg-yellow-100 rounded-full mb-4">
            <v-icon color="yellow" size="30">mdi-message-text</v-icon>
          </div>
          <h2 class="text-2xl font-bold text-slate-800  mb-4">Leave a Message</h2>
          <p class="text-gray-700 ">
            We’d love to hear from you! Leave a private message for the duck’s hider below, they can reply right here.
            Include your name and where you’re from!
          </p>

          <div class="mt-3 inline-flex items-center gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <v-icon size="18" color="primary">mdi-reply</v-icon>
            <span>The person who hid this duck can respond to your message here.</span>
          </div>
        </div>
        
        <Form @submit.prevent="handleSendMessage" class="max-w-xl mx-auto">
          <FormField name="message">
            <FormControl>
              <!-- Replace the two-way binding implementation with direct v-model -->
              <textarea 
                v-model="localMessage" 
                placeholder="Write your message here... The hider can reply to you here."
                class="min-h-[100px] sm:min-h-[120px] resize-none w-full p-3 sm:p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white/50 text-base"
                @blur="syncModelValue"
              ></textarea>
            </FormControl>
          </FormField>

          <div class="flex justify-center mt-4 sm:mt-6">
            <ConfettiButton
              type="submit"
              :disabled="!localMessage"
              class="w-full sm:w-auto text-white h-12 text-base sm:text-lg flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 rounded-lg shadow-lg transition-all duration-200 px-4 sm:px-8 disabled:opacity-50 disabled:cursor-not-allowed min-h-[50px]"
              @click="handleSendMessage"
            >
              <span>Send Message</span>
              <v-icon class="ml-2" size="20">mdi-send</v-icon>
            </ConfettiButton>
          </div>
        </Form>

        <!-- Message History Section -->
        <MessageHistory 
          v-if="showHistory"
          :messages="messages"
          :loading="loading"
          @refresh="$emit('refresh')"
          class="mt-8" 
        />
      </div>
    </div>
  </Motion>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, watch, onMounted } from 'vue';
import { Form, FormField, FormControl } from '@/components/ui/form';
import { Motion } from 'motion-v';
import type { Message } from '@/interfaces/message';

// Import async components
const ConfettiButton = defineAsyncComponent(() => import("@/components/ui/confettiButton").then(m => m.ConfettiButton));
const MessageHistory = defineAsyncComponent(() => import('@/components/duckview/MessageHistory.vue'));

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  messages: {
    type: Array as () => Message[],
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  showHistory: {
    type: Boolean,
    default: false
  },
  duck: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'send', 'refresh']);

// Use local state for better performance
const localMessage = ref('');

// Sync the local state with the prop initially and when prop changes
onMounted(() => {
  localMessage.value = props.modelValue;
});

watch(() => props.modelValue, (newVal) => {
  // Only update if values are different to prevent loops
  if (newVal !== localMessage.value) {
    localMessage.value = newVal;
  }
});

// Sync back to parent only when necessary
const syncModelValue = () => {
  if (props.modelValue !== localMessage.value) {
    emit('update:modelValue', localMessage.value);
  }
};

// Methods
const handleSendMessage = () => {
  // Make sure modelValue is updated before sending
  if (props.modelValue !== localMessage.value) {
    emit('update:modelValue', localMessage.value);
  }
  emit('send');
};
</script>

<style scoped>
/* Improved input and text styles */
textarea:focus {
  border-color: #eab308; /* yellow-500 */
  /* approximate Tailwind ring-2 ring-yellow-500/50 */
  box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.5);
}

/* Fix for mobile rendering performance */
textarea {
  -webkit-overflow-scrolling: touch;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  /* Prevent iOS font size adjustment */
  -webkit-text-size-adjust: 100%;
  /* Improve tap highlight */
  -webkit-tap-highlight-color: rgba(234, 179, 8, 0.3);
}

@media (max-width: 640px) {
  textarea {
    font-size: 16px !important; /* Prevents zoom on iOS */
    padding: 12px !important; /* Larger padding for better touch */
    min-height: 120px !important; /* Taller textarea on mobile */
  }
  
  /* Larger buttons on mobile for better touch targets */
  :deep(button) {
    min-height: 50px !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
}

/* Ensure ConfettiButton is touch-friendly */
:deep(.confetti-button) {
  touch-action: manipulation;
  min-height: 50px;
}

/* Optimize mobile form display */
@media (max-width: 480px) {
  .max-w-xl {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>